import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import baseApi from '@/api/client';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { useIsAuthenticated } from '@/auth/useIsAuthenticated';
import { env } from '@/env';
import type { Organization } from '@/types';

// ---------------------------------------------------------------------------
// IAM is the identity authority: it knows who the caller is and whether they
// are scoped at domain level (no tenant, no organisation), tenant level
// (tenant only) or organisation level (tenant + organisation). The backend
// re-derives the same scope server-side on every request — everything here
// only drives UI.

// The IAM service lives on the same host as CPMS but under the `/iam` base
// instead of `/cpms`, so derive it from VITE_CPMS_BACKEND_URL by swapping the
// trailing segment.
export function iamUrl(path: string): string {
  const cpmsBase = env.VITE_CPMS_BACKEND_URL;
  if (!cpmsBase) throw new Error('VITE_CPMS_BACKEND_URL is not configured');
  const iamBase = cpmsBase.replace(/\/cpms\/?$/, '/iam');
  return `${iamBase}${path}`;
}

/**
 * IAM lists may come back as a plain array or wrapped in a Spring-style page
 * (`{ content: [...] }`) — accept both. Every caller fetches with
 * `size: IAM_MAX_PAGE_SIZE` and treats the answer as the whole list, so a
 * multi-page envelope means silent truncation — warn instead of hiding it.
 */
export function unwrapIamList<T>(data: T[] | { content: T[]; totalPages?: number }): T[] {
  if (Array.isArray(data)) return data;
  if ((data.totalPages ?? 1) > 1) {
    console.warn(
      `IAM list is paged (${data.totalPages} pages) but only the first page was fetched; the list is truncated.`,
    );
  }
  return data.content ?? [];
}

// IAM pages its lists (Spring pageable, default 20 rows). The pickers need the whole
// estate, so ask for IAM's maximum page in one request.
export const IAM_MAX_PAGE_SIZE = 2000;

// --- Shape returned by IAM `GET /rest/current-user` (only the fields we read) ---

interface CurrentUserPermission {
  permissionId: string;
  subscriptionId: string;
}

interface CurrentUserRoleLabel {
  label: string;
  value: string;
}

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  type: string;
  tenantId?: string | null;
  tenantName?: string | null;
  organisationId?: string | null;
  organisationName?: string | null;
  roleLabels: CurrentUserRoleLabel[];
}

export interface CurrentUserResponse {
  permissions: CurrentUserPermission[];
  roleRanking: number;
  user: CurrentUser;
}

export type AccessLevel =
  | { level: 'domain' }
  | { level: 'tenant'; tenantId: string; tenantName?: string }
  | {
      level: 'organisation';
      organisationId: string;
      organisationName?: string;
      tenantId?: string;
      tenantName?: string;
    };

/**
 * Derive the user's scope from which ids the IAM response carries: an organisation id
 * means organisation level, otherwise a tenant id means tenant level, otherwise domain.
 * Names are carried through when present (spread conditionally — `exactOptionalPropertyTypes`
 * rejects an explicit `undefined`).
 */
export function resolveAccessLevel(user: CurrentUser): AccessLevel {
  if (user.organisationId) {
    return {
      level: 'organisation',
      organisationId: user.organisationId,
      ...(user.organisationName ? { organisationName: user.organisationName } : {}),
      ...(user.tenantId ? { tenantId: user.tenantId } : {}),
      ...(user.tenantName ? { tenantName: user.tenantName } : {}),
    };
  }
  if (user.tenantId) {
    return {
      level: 'tenant',
      tenantId: user.tenantId,
      ...(user.tenantName ? { tenantName: user.tenantName } : {}),
    };
  }
  return { level: 'domain' };
}

/**
 * The IAM target a caller's *new* entries are filed under, for the modules whose
 * mutations go to `api/orgs/{orgId}/…`. That `{orgId}` is really an IAM *target* id —
 * a tenant's own id authorizes there (`IamScope.From` puts it first in `TargetIds`) —
 * and visibility follows the bucket: whoever may see the bucket sees, and may edit,
 * what is filed in it.
 *
 * So a tenant files under their tenant id and an organisation under their own id —
 * **never** under the shell's selected organisation. There is no organisation picker;
 * the store auto-selects a tenant caller's *first* organisation, and filing a tenant's
 * entries there exposed them to that organisation's users while hiding them from the
 * tenant's other organisations (a split-billing user's bank account included). Only a
 * domain caller, who has no id of their own, keeps the selected organisation as an
 * incidental bucket.
 */
export function resolveFilingTarget(
  access: AccessLevel,
  selectedOrgId: string | undefined,
): string | undefined {
  switch (access.level) {
    case 'tenant':
      return access.tenantId;
    case 'organisation':
      return access.organisationId;
    case 'domain':
      return selectedOrgId;
  }
}

/** The signed-in user's IAM profile (identity + tenant/organisation scope). */
export function useIamUser(): UseQueryResult<CurrentUserResponse> {
  const isAuthenticated = useIsAuthenticated();
  // Scope the cache to the signed-in Cognito identity so a previous user's IAM
  // profile is never served to a different user after a logout→login on the
  // same QueryClient.
  //
  // Keyed on `userId` (the Cognito subject) rather than the sign-in email: Amplify only
  // populates `signInDetails` on a fresh in-page sign-in, so after a page reload the
  // same person rehydrates without it and the old fallback dropped to `username` — a
  // different string for the same user, which re-keyed the entry and refetched the
  // profile for no reason. `userId` is the one identifier present on both paths.
  const { user } = useCurrentUser();
  const signedInUserId = user?.userId ?? '';
  return useQuery({
    queryKey: ['iam', 'current-user', signedInUserId],
    queryFn: async () => {
      // Absolute URL → axios ignores the client's baseURL, while the request
      // interceptor still attaches the bearer token.
      const { data } = await baseApi.get<CurrentUserResponse>(iamUrl('/rest/current-user'));
      return data;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });
}

// IAM's organisation DTO (subset — only what the pickers need).
interface IamOrganisationDto {
  id: string;
  name: string;
  tenantId?: string | null;
}

interface IamTenantDto {
  id: string;
  name: string;
}

/**
 * A tenant or an organisation, as something CMX data can be filed under. Both are
 * opaque IAM ids drawn from the same space, so several modules store one column that
 * may hold either (helpdesk's `operator_id`, the split-billing service fee's
 * `target_id`) — `kind` is what tells them apart for display.
 */
export interface IamTarget {
  kind: 'tenant' | 'organisation';
  id: string;
  name: string;
  /** Set for organisations that report a tenant — drives tenant → organisation pickers. */
  tenantId?: string;
}

/**
 * Every tenant and organisation, for pickers that can target either. **Domain-level
 * data** — the endpoints list the whole estate — so callers pass `enabled` from their
 * access level rather than firing requests that will 403.
 *
 * The invoices module has its own near-identical `useMappingTargets`; this is the
 * shared version, and that one can fold into it when someone touches it next.
 */
export function useIamTargets(enabled = true): UseQueryResult<IamTarget[]> {
  const isAuthenticated = useIsAuthenticated();
  return useQuery({
    queryKey: ['iam', 'targets'],
    queryFn: async (): Promise<IamTarget[]> => {
      const [tenantsRes, orgsRes] = await Promise.all([
        baseApi.get<IamTenantDto[] | { content: IamTenantDto[] }>(iamUrl('/rest/tenants'), {
          params: { size: IAM_MAX_PAGE_SIZE },
        }),
        baseApi.get<IamOrganisationDto[] | { content: IamOrganisationDto[] }>(
          iamUrl('/rest/organisations'),
          { params: { size: IAM_MAX_PAGE_SIZE } },
        ),
      ]);
      // Ordered by name, because every picker on this list shows the name. Sorted here
      // rather than asked of IAM: the whole estate already arrives in one
      // `size=IAM_MAX_PAGE_SIZE` request (a server sort would save no round trip, the way
      // it does for the paged CPMS fleet query in `@/api/cpms`), and `localeCompare`
      // orders accented and mixed-case names the way a reader expects rather than by the
      // column's collation.
      const byName = <T extends { name: string }>(a: T, b: T) => a.name.localeCompare(b.name);
      return [
        ...unwrapIamList(tenantsRes.data)
          .sort(byName)
          .map<IamTarget>((tenant) => ({
            kind: 'tenant',
            id: tenant.id,
            name: tenant.name,
          })),
        ...unwrapIamList(orgsRes.data)
          .sort(byName)
          .map<IamTarget>((org) => ({
            kind: 'organisation',
            id: org.id,
            name: org.name,
            // Spread conditionally — `exactOptionalPropertyTypes` rejects an explicit undefined.
            ...(org.tenantId ? { tenantId: org.tenantId } : {}),
          })),
      ];
    },
    enabled: isAuthenticated && enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * The organisations the caller may select, per their access level: domain → all
 * organisations, tenant → their tenant's organisations, organisation → their own
 * (fixed — no fetch). Drives the sidebar organisation picker.
 */
export function useIamOrganisations(
  access: AccessLevel | undefined,
): UseQueryResult<Organization[]> {
  const isAuthenticated = useIsAuthenticated();
  // The list depends on the caller's exact scope, so the key must carry the scope's
  // own id — not just its level. A tenant caller keys on their tenant id; an
  // organisation caller on their organisation id. Keying on level alone meant every
  // organisation-level caller shared one entry (`…,'organisation',''`), so after a
  // silent re-login as a different organisation user (session expiry doesn't run
  // queryClient.clear() — only the explicit sign-out button does) the previous user's
  // organisation was served from cache and became the bucket new mutations targeted.
  const scopeId =
    access?.level === 'tenant'
      ? access.tenantId
      : access?.level === 'organisation'
        ? access.organisationId
        : '';
  return useQuery({
    queryKey: ['iam', 'organisations', access?.level ?? '', scopeId],
    queryFn: async (): Promise<Organization[]> => {
      if (!access) return [];
      if (access.level === 'organisation') {
        return [
          { id: access.organisationId, name: access.organisationName ?? access.organisationId },
        ];
      }
      const path =
        access.level === 'tenant'
          ? `/rest/tenants/${encodeURIComponent(access.tenantId)}/organisations`
          : '/rest/organisations';
      const { data } = await baseApi.get<IamOrganisationDto[] | { content: IamOrganisationDto[] }>(
        iamUrl(path),
        { params: { size: IAM_MAX_PAGE_SIZE } },
      );
      return unwrapIamList(data).map((o) => ({ id: o.id, name: o.name }));
    },
    enabled: isAuthenticated && !!access,
    staleTime: 5 * 60_000,
  });
}
